import type { Database } from '@areapif/areapif-shared'
import { useCallback, useState } from 'react'

import type { SealMetadata } from '../domain/evidenceUpload'
import {
  buildInspectionPhoto,
  buildInspectionSeal,
  generateStorageName,
  mergePhotos,
  mergeSeals,
  validateFile,
} from '../domain/evidenceUpload'
import { fetchExistingPhotos, fetchExistingSeals, removeFiles, updatePhotos, updateSeals, uploadFile } from '../models/evidenceUpload'

export type { SealMetadata } from '../domain/evidenceUpload'

type PhotoCategory = Database['public']['Enums']['photo_category']
type SealCategory = Database['public']['Enums']['seal_category']

export interface UseEvidenceUploadResult {
  uploadPhotos: (inspectionId: string, files: File[], category: PhotoCategory) => Promise<void>
  uploadSeals: (inspectionId: string, files: File[], category: SealCategory, metadata: SealMetadata) => Promise<void>
  isUploading: boolean
  error: string | null
}

/**
 * Hook for uploading photo and seal evidence to historical inspections.
 * Uploads files to Supabase Storage under the 'inspections' bucket and
 * appends the new entries to the corresponding record arrays.
 *
 * Note: The read-then-update pattern for composite type arrays is not atomic.
 * Concurrent uploads to the same inspection could cause data loss. For this
 * backoffice tool with low concurrency, this is an acceptable trade-off.
 * A server-side Postgres function using array_cat would be the robust solution.
 */
export const useEvidenceUpload = (): UseEvidenceUploadResult => {
  const [isUploading, setIsUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const uploadPhotos = useCallback(async (inspectionId: string, files: File[], category: PhotoCategory): Promise<void> => {
    setIsUploading(true)
    setError(null)

    const uploadedPaths: string[] = []

    try {
      for (const file of files) {
        validateFile(file)
        const storageName = generateStorageName(file.name)
        const uploadedPath = await uploadFile(inspectionId, file, storageName)
        uploadedPaths.push(uploadedPath)
      }

      const existingPhotos = await fetchExistingPhotos(inspectionId)
      const newPhotos = uploadedPaths.map(path => buildInspectionPhoto(path, category))
      const mergedPhotos = mergePhotos(existingPhotos, newPhotos)

      await updatePhotos(inspectionId, mergedPhotos)
    } catch (err) {
      await removeFiles(uploadedPaths)
      const message = err instanceof Error ? err.message : 'Unknown error during photo upload'
      setError(message)
      throw err
    } finally {
      setIsUploading(false)
    }
  }, [])

  const uploadSeals = useCallback(
    async (inspectionId: string, files: File[], category: SealCategory, metadata: SealMetadata): Promise<void> => {
      setIsUploading(true)
      setError(null)

      const uploadedPaths: string[] = []

      try {
        for (const file of files) {
          validateFile(file)
          const storageName = generateStorageName(file.name)
          const uploadedPath = await uploadFile(inspectionId, file, storageName)
          uploadedPaths.push(uploadedPath)
        }

        const existingSeals = await fetchExistingSeals(inspectionId)
        const newSeals = uploadedPaths.map(path => buildInspectionSeal(path, category, metadata))
        const mergedSeals = mergeSeals(existingSeals, newSeals)

        await updateSeals(inspectionId, mergedSeals)
      } catch (err) {
        await removeFiles(uploadedPaths)
        const message = err instanceof Error ? err.message : 'Unknown error during seal upload'
        setError(message)
        throw err
      } finally {
        setIsUploading(false)
      }
    },
    [],
  )

  return { uploadPhotos, uploadSeals, isUploading, error }
}
